import { useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import { MUSIC_RELEASES } from "../data/music";
import { T } from "@/locales/tr";

const fullTitle = (r) => (r.version ? `${T(r.title)} — ${r.version}` : r.title);

function ReleaseCard({ r }) {
  const title = fullTitle(r);
  return (
    <article data-testid={`music-release-${r.id}`} aria-labelledby={`music-title-${r.id}`} className="bg-[#0b0b0c] flex flex-col">
      <div className="bg-[#0a0a0b] border-b border-white/10">
        <img src={r.cover} alt={T("Pochette — {t}").replace("{t}", r.title)} loading="lazy" className="w-full aspect-square object-contain" />
      </div>
      <div className="p-6 flex-1 flex flex-col gap-4">
        <div>
          {r.version && <div className="papicoo-kicker">{r.version}</div>}
          <h2 id={`music-title-${r.id}`} className="font-display font-semibold uppercase text-xl text-white mt-2">{T(r.title)}</h2>
        </div>
        <dl className="grid grid-cols-2 gap-3 text-xs">
          <div>
            <dt className="uppercase tracking-[0.2em] text-zinc-500">{T("Extrait")}</dt>
            <dd data-testid={`music-excerpt-${r.id}`} className="text-zinc-200 mt-1">{T("{n} secondes").replace("{n}", r.excerptSeconds)}</dd>
          </div>
          <div>
            <dt className="uppercase tracking-[0.2em] text-zinc-500">{T("Durée complète")}</dt>
            <dd data-testid={`music-duration-${r.id}`} className="text-zinc-200 mt-1">{r.durationLabel}</dd>
          </div>
        </dl>
        <audio
          data-testid={`music-audio-${r.id}`}
          controls
          preload="none"
          src={r.audio}
          aria-label={T("Écouter l’extrait de 30 secondes — {t}").replace("{t}", title)}
          className="w-full mt-auto"
        >
          {T("Ton navigateur ne lit pas l’audio.")}
        </audio>
      </div>
    </article>
  );
}

// Une seule lecture à la fois : démarrer un extrait met les autres en pause
function useSinglePlayback(ref) {
  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const onPlay = (e) => {
      root.querySelectorAll("audio").forEach((a) => { if (a !== e.target) a.pause(); });
    };
    root.addEventListener("play", onPlay, true);
    return () => root.removeEventListener("play", onPlay, true);
  }, [ref]);
}

export default function Music() {
  const listRef = useRef(null);
  useSinglePlayback(listRef);
  if (MUSIC_RELEASES.length === 0) return <Navigate to="/" replace />;
  return (
    <div data-testid="page-music" className="max-w-6xl mx-auto px-5 sm:px-8 pt-16 pb-32">
      <div className="papicoo-kicker">{T("Papicoo · Musique")}</div>
      <h1 className="font-display font-semibold text-white mt-3">{T("Musique — Les sorties Papicoo")}</h1>
      <p className="mt-5 text-sm text-zinc-400 max-w-xl">{T("Écoute un extrait de 30 secondes de chaque titre. Les durées indiquées sont celles des versions complètes.")}</p>
      <div ref={listRef} className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/10">
        {MUSIC_RELEASES.map((r) => <ReleaseCard key={r.id} r={r} />)}
      </div>
      <p data-testid="music-sale-notice" className="mt-8 text-xs text-zinc-500">{T("Vente des titres complets en préparation.")}</p>
    </div>
  );
}
